import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom'; // Added for URL parameter extraction
import { CheckCircle, Calendar, ArrowRight, FileText, ShieldCheck } from 'lucide-react';

/**
 * Micheals Infrastructure Advisory - AUTHORIZATION CONFIRMED
 * ROLE: Post-Payment Handoff & Strategic Onboarding.
 * VERSION: 3.3.0 (Build 2026.03.23 - Strategic Reference Integration)
 */

const Success = () => {
  const location = useLocation();
  
  // Extract the briefId from the URL to validate the session visually for the client
  const queryParams = new URLSearchParams(location.search);
  const briefId = queryParams.get('briefId') || "PENDING_HANDSHAKE";

  useEffect(() => {
    // Clear the session data to prevent duplicate logic on refresh
    sessionStorage.removeItem('leadEmail');
    
    // Telemetry Signal for the Intent Monitor
    console.log(`🛡️ [STRIPE_SUCCESS] Authorization Confirmed for Brief: ${briefId}. Session Purged.`);
    
    // Optional: Scroll to top to ensure the animation is visible
    window.scrollTo(0, 0);
  }, [briefId]);

  // Generate a pseudo-forensic reference ID if no briefId is present
  const referenceId = React.useMemo(() => 
    briefId !== "PENDING_HANDSHAKE" ? briefId : `MED-${Math.random().toString(36).toUpperCase().substring(2, 10)}`, 
  [briefId]);

  return (
    <div className="min-h-screen bg-[#f8f8f7] flex items-center justify-center p-6 selection:bg-stone-200">
      <Helmet>
        <title>Authorization Confirmed | Micheals Infrastructure Advisory</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      
      <motion.div 
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="max-w-2xl w-full bg-white rounded-[2.5rem] shadow-[0_40px_100px_-20px_rgba(0,0,0,0.1)] border border-stone-200 overflow-hidden"
      >
        {/* Success Header: Architectural Slate */}
        <div className="bg-stone-900 p-12 text-center relative overflow-hidden">
          <motion.div 
            initial={{ scale: 0, rotate: -45 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: "spring", stiffness: 200, damping: 15, delay: 0.2 }}
            className="w-20 h-20 bg-amber-700 rounded-full flex items-center justify-center mx-auto mb-6 relative z-10 shadow-lg"
          >
            <CheckCircle className="text-white w-10 h-10" />
          </motion.div>
          
          <h1 className="text-3xl md:text-4xl font-serif italic text-white relative z-10">
            Phase 02 Authorized
          </h1>
          <p className="text-stone-400 uppercase tracking-[0.3em] text-[10px] mt-3 font-bold relative z-10">
            Strategic Consultation Handshake Complete
          </p>
          
          {/* Subtle Grid Background Pattern */}
          <div className="absolute inset-0 opacity-[0.03] pointer-events-none">
             <div className="absolute inset-0" style={{ 
               backgroundImage: 'linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)', 
               backgroundSize: '30px 30px' 
             }} />
          </div>
        </div>

        {/* Next Steps Body */}
        <div className="p-10 md:p-14 space-y-12">
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <ShieldCheck size={16} className="text-amber-700" />
              <h3 className="text-stone-900 font-bold uppercase tracking-widest text-xs">
                Immediate Protocol:
              </h3>
            </div>
            
            <div className="grid gap-6">
              {/* Step 1: Outreach */}
              <motion.div 
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 }}
                className="flex gap-5 p-6 bg-stone-50 rounded-2xl border border-stone-100 hover:border-stone-200 transition-colors"
              >
                <Calendar className="text-stone-900 shrink-0" size={22} />
                <div>
                  <p className="text-sm font-bold text-stone-900">Coordination Outreach</p>
                  <p className="text-xs text-stone-500 mt-1 leading-relaxed">
                    Operator will contact you within 24 business hours to finalize the 60-minute technical briefing window.
                  </p>
                </div>
              </motion.div>
              
              {/* Step 2: Documentation */}
              <motion.div 
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 }}
                className="flex gap-5 p-6 bg-stone-50 rounded-2xl border border-stone-100 hover:border-stone-200 transition-colors"
              >
                <FileText className="text-stone-900 shrink-0" size={22} />
                <div>
                  <p className="text-sm font-bold text-stone-900">Technical Briefing Prep</p>
                  <p className="text-xs text-stone-500 mt-1 leading-relaxed">
                    Please ensure all site schematics, vendor contracts, or architectural designs are ready for forensic design review.
                  </p>
                </div>
              </motion.div>
            </div>
          </div>

          {/* Footer Actions */}
          <div className="pt-8 border-t border-stone-100 flex flex-col md:flex-row gap-6 items-center justify-between">
            <div className="text-left">
              <p className="text-[10px] text-stone-400 uppercase tracking-tighter">Handshake Reference</p>
              <p className="text-[11px] font-mono text-stone-900 font-bold tracking-tight">{referenceId}</p>
            </div>
            
            <a 
              href="/"
              className="group flex items-center gap-2 px-8 py-4 bg-stone-900 text-white rounded-full text-[11px] font-bold uppercase tracking-widest hover:bg-amber-800 transition-all duration-300 active:scale-95"
            >
              Return to Headquarters 
              <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
            </a>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Success;